/**
 * Client for the feat-053b Pilot Dashboard endpoints (routes/pilot.py).
 *
 * Contracts:
 *  - Spec:    worktrees/feat-053b-pilot/spec.md
 *  - ADR 0001 (decisions/0001-pilot-metrics.md) — D1 Ring/IPT formulas, D3 the
 *    placeholder==real contract (every payload carries `placeholder` + `data_source`),
 *    D5 targets/annualized, D6 adjustments, D7 session-only view state.
 *
 * THE HONESTY CRUX (ADR 0001 D3 + ADR 0002 addendum, design mockup §A/§B): in b1
 * the Ring/IPT absolute `value` + `series` are REAL (`pos_real`), while the
 * COMPARISON — delta, "vs last year" baseline, target band, annualized — is SEEDED.
 * Per ADR 0002 the MetricBlock carries TWO flag pairs: `placeholder`/`data_source`
 * govern the HEADLINE value (b1 Ring/IPT = false/pos_real, or no_coverage→null),
 * and `baseline_placeholder`/`baseline_data_source` govern the COMPARISON layer
 * (b1 = true/seeded_baseline). The slate "Estimated" marker keys off
 * `baseline_placeholder` — the delta renders in `preview` slate, NEVER success-green;
 * `no_coverage` (value null) renders an honest "—". The marker attaches to the
 * comparison, not the real absolute number.
 *
 * The frontend NEVER recomputes a metric (feedback_metrics_single_source): it
 * renders server-formatted `*_display` strings and the server-stamped flags verbatim.
 *
 * Fake mode (`config.environment === 'fake'`, npm run start:fake): every call
 * returns deterministic canned data matching the design mock so the whole screen
 * renders with no backend. `?pilot_state=<state>` overrides the tenant/coverage
 * branch so QA can flip between full / no-coverage / non-pilot.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';
import { createLimiter } from './concurrencyLimiter';

// ---------- Domain types (mirror ADR D3) ----------

export type PilotView = 'cumulative' | 'today';
export type PilotCompare = 'last_year' | 'control';

export type DataSource =
  | 'pos_real'
  // feat-059 (ADR 0007 D2/D7): real control-fleet baseline. Renders identically to
  // `pos_real` — real, non-placeholder. Distinct on the wire only so telemetry can
  // tell a control-derived baseline apart from a pilot-prior-year one (the proof the
  // silent-relabel bug is fixed). The frontend treats it as real, never placeholder.
  | 'pos_real_control'
  | 'seeded_baseline'
  | 'seeded_targets'
  | 'seeded_trend'
  | 'seeded_adjustments'
  | 'seeded'
  | 'no_coverage'
  | 'manager_entered';

// De-facto wire form is SPACES (backend emit + design mock + QA-verified tests).
// ADR 0002's underscore prose is being cleaned up to match; BandBar humanizes
// underscores defensively anyway, so the real path renders correctly either way.
export type BandState = 'below band' | 'in band' | 'above band';

export interface PilotBand {
  low: number;
  high: number;
  unit: 'pct' | 'abs';
  // ADR 0002 — explicit viz coordinates, all on a 0-100 track (server-authoritative,
  // present in both real and seeded responses; QA asserts they are 0-100):
  low_pos: number;   // band-edge marker positions
  high_pos: number;
  fill: number;      // current delta as a 0-100 TRACK position (NOT within-band)
  state: BandState;
  display: string;   // preformatted "+2% – +5%" / "+1.2 – +1.5"
  /** Band copy is target config — seeded until per-pilot targets are set. */
  placeholder: boolean;
  data_source: DataSource;
}

export interface SeriesPoint {
  date: string;
  value: number | null; // null → gap in the spark/trend line
}

export interface PilotMetric {
  /** Headline number — REAL POS in b1 (null only when no_coverage). */
  value: number | null;
  value_display: string; // "$18.42" | "2.18" | "—"
  /** Comparison vs baseline (SEEDED in b1). */
  delta_pct: number | null;
  delta_pct_display: string | null; // "+3.1%"
  delta_usd: number | null;
  delta_usd_display: string | null; // "+$0.55"
  baseline: number | null;
  baseline_display: string | null; // "$17.87"
  baseline_label: string; // "last year" | "control fleet"
  band: PilotBand | null;
  series: SeriesPoint[];
  /** Spark Y-bounds (server-computed so React never derives scale). */
  spark_min: number;
  spark_max: number;
  /**
   * TWO flag pairs (backend-emitted + QA-endorsed; refines ADR D3's single flag
   * for b1 where the value is real but the comparison is seeded):
   *  - `placeholder`/`data_source` → the HEADLINE value: `pos_real` (real number)
   *    or `no_coverage` (value null → honest "—" + Awaiting POS).
   *  - `baseline_placeholder`/`baseline_data_source` → the SEEDED COMPARISON layer
   *    (baseline, delta, band): `seeded_baseline` in b1, `manager_entered`/false
   *    once a baseline override is configured.
   * The slate Estimated marker keys off `baseline_placeholder`; the "—" keys off
   * `data_source === 'no_coverage'`. (annualized is its own scorecard object.)
   */
  placeholder: boolean;
  data_source: DataSource;
  baseline_placeholder: boolean;
  baseline_data_source: DataSource;
}

export interface PilotAnnualized {
  value_display: string; // "$80,120"
  placeholder: boolean;
  data_source: DataSource;
}

export interface PilotHeader {
  iteration: string; // "Iteration 3"
  day_of: number; // 27
  pilot_length: number; // 60
  store_count: number; // 3
  updated_at: string | null; // ISO; rendered "Last updated …"
  updated_at_display: string | null; // server-formatted "Tue May 19, 11:42 AM PT"
}

export interface PilotScorecard {
  is_pilot_tenant: boolean;
  header: PilotHeader;
  ring: PilotMetric;
  ipt: PilotMetric;
  annualized: PilotAnnualized;
}

export interface TrendSeries {
  this_year: Array<number | null>;
  baseline: Array<number | null>; // last-year OR control, per compare param
  y_min: number;
  y_max: number;
  fmt: 'currency' | 'decimal';
}

export interface TrendIteration {
  day_index: number;
  label: string;
}

export interface PilotTrend {
  dates: string[];
  iterations: TrendIteration[];
  ring: TrendSeries;
  ipt: TrendSeries;
  baseline_label: string; // "last year" | "control"
  placeholder: boolean;
  data_source: DataSource;
}

export interface PerStoreRow {
  store: string;
  ring_display: string; // "$18.91" | "—"
  ring_delta_display: string | null; // "+4.2%" | null
  ipt_display: string; // "2.24" | "—"
  ipt_delta_display: string | null; // "+0.27" | null
  placeholder: boolean; // seeded comparison
  data_source: DataSource; // 'no_coverage' for a thin store
}

export interface PerStore {
  rows: PerStoreRow[];
  total: PerStoreRow; // "Pilot total" row
  /** Whether ANY row's comparison is seeded → drives the one panel-header chip. */
  placeholder: boolean;
  data_source: DataSource;
}

export interface AdjustmentRow {
  id: string;
  description: string;
  note: string | null;
  /** Server-formatted impact ("+$0.37" | "flagged" | "no net effect"). */
  impact_display: string;
  /** true when impact is the free-text "flagged" label (gold), not a $ figure. */
  flagged: boolean;
  created_by_name: string | null;
  effective_date: string | null;
}

export interface PilotAdjustments {
  rows: AdjustmentRow[];
  net_display: string; // "+$0.45"
  placeholder: boolean; // true when the list is the seeded sample
  data_source: DataSource;
}

// ---------- HTTP plumbing (mirrors squareConnectionService) ----------

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

// feat-085 P2-B: cap how many pilot endpoints are in flight at once so the
// dashboard never bursts all of {scorecard, trend, per-store, adjustments}
// concurrently onto the DB pool. The headline (scorecard) is requested first and
// the heavy panels are deferred until revealed (see PilotDashboardPage), so the
// queue mostly serializes the residual handful behind the headline.
export const MAX_CONCURRENT_PILOT_REQUESTS = 2;
const pilotRequestLimiter = createLimiter(MAX_CONCURRENT_PILOT_REQUESTS);

async function api<T>(path: string): Promise<T> {
  return pilotRequestLimiter(async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...authHeader(),
    };
    const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
    if (resp.status === 404) {
      // Non-pilot tenant — caller maps to the "No pilot configured" empty state.
      throw new PilotNotConfiguredError();
    }
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`API ${path} ${resp.status}: ${text}`);
    }
    return resp.json();
  });
}

export class PilotNotConfiguredError extends Error {
  constructor() {
    super('No pilot configured for this tenant');
    this.name = 'PilotNotConfiguredError';
  }
}

const isFake = (): boolean => config.environment === 'fake';

function fakeStateOverride(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('pilot_state');
  } catch {
    return null;
  }
}

// ---------- Fake-mode deterministic data (matches the design mock) ----------
// Numbers mirror the export's METRICS_DATA / TREND / SPARK so the fake screen
// matches estimated-marker-mockup.html. Comparison fields carry placeholder
// flags per b1 (ADR D3); Ring/IPT value+series are pos_real.

const FAKE_SPARK_DATES = [
  'Wed May 13', 'Thu May 14', 'Fri May 15', 'Sat May 16', 'Sun May 17', 'Mon May 18', 'Tue May 19',
];

function ringSpark(values: number[]): SeriesPoint[] {
  return values.map((v, i) => ({ date: FAKE_SPARK_DATES[i], value: v }));
}

interface FakeMetricSeed {
  value_display: string;
  delta_pct_display: string;
  delta_usd_display: string;
  baseline_display: string;
  baseline_label: string;
  band: { low: number; high: number; unit: 'pct' | 'abs'; low_pos: number; high_pos: number; fill: number; state: BandState; display: string };
  spark: number[];
  spark_min: number;
  spark_max: number;
}

function metric(seed: FakeMetricSeed): PilotMetric {
  return {
    value: parseFloat(seed.value_display.replace(/[^0-9.]/g, '')),
    value_display: seed.value_display,
    delta_pct: parseFloat(seed.delta_pct_display),
    delta_pct_display: seed.delta_pct_display,
    delta_usd: parseFloat(seed.delta_usd_display.replace(/[^0-9.-]/g, '')),
    delta_usd_display: seed.delta_usd_display,
    baseline: parseFloat(seed.baseline_display.replace(/[^0-9.]/g, '')),
    baseline_display: seed.baseline_display,
    baseline_label: seed.baseline_label,
    band: {
      ...seed.band,
      placeholder: true,
      data_source: 'seeded_targets',
    },
    series: ringSpark(seed.spark),
    spark_min: seed.spark_min,
    spark_max: seed.spark_max,
    // b1: headline value+series are REAL POS…
    placeholder: false,
    data_source: 'pos_real',
    // …but the comparison layer (baseline/delta/band) is seeded → marker keys here.
    baseline_placeholder: true,
    baseline_data_source: 'seeded_baseline',
  };
}

const FAKE_SCORECARD: Record<PilotView, Record<PilotCompare, { ring: FakeMetricSeed; ipt: FakeMetricSeed; annualized: string }>> = {
  cumulative: {
    last_year: {
      ring: { value_display: '$18.42', delta_pct_display: '+3.1%', delta_usd_display: '+$0.55', baseline_display: '$17.87', baseline_label: 'last year', band: { low: 2, high: 5, unit: 'pct', low_pos: 40, high_pos: 100, fill: 62, state: 'in band', display: '+2% – +5%' }, spark: [18.95, 19.02, 19.08, 19.15, 19.22, 19.28, 19.32], spark_min: 18.8, spark_max: 19.45 },
      ipt: { value_display: '2.18', delta_pct_display: '+10.7%', delta_usd_display: '+0.21', baseline_display: '1.97', baseline_label: 'last year', band: { low: 1.2, high: 1.5, unit: 'abs', low_pos: 80, high_pos: 100, fill: 14, state: 'below band', display: '+1.2 – +1.5' }, spark: [2.23, 2.25, 2.27, 2.28, 2.29, 2.30, 2.32], spark_min: 2.18, spark_max: 2.36 },
      annualized: '$80,120',
    },
    control: {
      ring: { value_display: '$18.42', delta_pct_display: '+1.8%', delta_usd_display: '+$0.33', baseline_display: '$18.09', baseline_label: 'control fleet', band: { low: 2, high: 5, unit: 'pct', low_pos: 40, high_pos: 100, fill: 0, state: 'below band', display: '+2% – +5%' }, spark: [18.95, 19.02, 19.08, 19.15, 19.22, 19.28, 19.32], spark_min: 18.8, spark_max: 19.45 },
      ipt: { value_display: '2.18', delta_pct_display: '+4.3%', delta_usd_display: '+0.09', baseline_display: '2.09', baseline_label: 'control fleet', band: { low: 1.2, high: 1.5, unit: 'abs', low_pos: 80, high_pos: 100, fill: 0, state: 'below band', display: '+1.2 – +1.5' }, spark: [2.23, 2.25, 2.27, 2.28, 2.29, 2.30, 2.32], spark_min: 2.18, spark_max: 2.36 },
      annualized: '$48,200',
    },
  },
  today: {
    last_year: {
      ring: { value_display: '$18.71', delta_pct_display: '+4.7%', delta_usd_display: '+$0.84', baseline_display: '$17.87', baseline_label: 'last year', band: { low: 2, high: 5, unit: 'pct', low_pos: 40, high_pos: 100, fill: 78, state: 'in band', display: '+2% – +5%' }, spark: [18.95, 19.02, 19.08, 19.15, 19.22, 19.28, 19.32], spark_min: 18.8, spark_max: 19.45 },
      ipt: { value_display: '2.28', delta_pct_display: '+15.7%', delta_usd_display: '+0.31', baseline_display: '1.97', baseline_label: 'last year', band: { low: 1.2, high: 1.5, unit: 'abs', low_pos: 80, high_pos: 100, fill: 18, state: 'below band', display: '+1.2 – +1.5' }, spark: [2.23, 2.25, 2.27, 2.28, 2.29, 2.30, 2.32], spark_min: 2.18, spark_max: 2.36 },
      annualized: '$94,300',
    },
    control: {
      ring: { value_display: '$18.71', delta_pct_display: '+3.4%', delta_usd_display: '+$0.62', baseline_display: '$18.09', baseline_label: 'control fleet', band: { low: 2, high: 5, unit: 'pct', low_pos: 40, high_pos: 100, fill: 28, state: 'in band', display: '+2% – +5%' }, spark: [18.95, 19.02, 19.08, 19.15, 19.22, 19.28, 19.32], spark_min: 18.8, spark_max: 19.45 },
      ipt: { value_display: '2.28', delta_pct_display: '+9.1%', delta_usd_display: '+0.19', baseline_display: '2.09', baseline_label: 'control fleet', band: { low: 1.2, high: 1.5, unit: 'abs', low_pos: 80, high_pos: 100, fill: 0, state: 'below band', display: '+1.2 – +1.5' }, spark: [2.23, 2.25, 2.27, 2.28, 2.29, 2.30, 2.32], spark_min: 2.18, spark_max: 2.36 },
      annualized: '$61,400',
    },
  },
};

function fakeHeader(): PilotHeader {
  return {
    iteration: 'Iteration 3',
    day_of: 27,
    pilot_length: 60,
    store_count: 3,
    updated_at: null,
    updated_at_display: 'Tue May 19, 11:42 AM PT',
  };
}

function noCoverageMetric(): PilotMetric {
  return {
    value: null,
    value_display: '—',
    delta_pct: null,
    delta_pct_display: null,
    delta_usd: null,
    delta_usd_display: null,
    baseline: null,
    baseline_display: null,
    baseline_label: 'last year',
    band: null,
    series: [],
    spark_min: 0,
    spark_max: 1,
    placeholder: true,
    data_source: 'no_coverage',
    baseline_placeholder: true,
    baseline_data_source: 'no_coverage',
  };
}

// ---------- Public API ----------

export async function getPilotScorecard(
  tenantId: string,
  view: PilotView,
  compare: PilotCompare,
): Promise<PilotScorecard> {
  if (isFake()) {
    const override = fakeStateOverride();
    if (override === 'none') throw new PilotNotConfiguredError();
    const seed = FAKE_SCORECARD[view][compare];
    const ipt = override === 'no_coverage' ? noCoverageMetric() : metric(seed.ipt);
    return {
      is_pilot_tenant: true,
      header: fakeHeader(),
      ring: metric(seed.ring),
      ipt,
      annualized: {
        value_display: seed.annualized,
        placeholder: true,
        data_source: 'seeded',
      },
    };
  }
  return api<PilotScorecard>(
    `/api/pilot/${encodeURIComponent(tenantId)}/scorecard?view=${view}&compare=${compare}`,
  );
}

const FAKE_TREND_DATES = [
  'Wed Apr 22', 'Thu Apr 23', 'Fri Apr 24', 'Sat Apr 25', 'Sun Apr 26', 'Mon Apr 27', 'Tue Apr 28',
  'Wed Apr 29', 'Thu Apr 30', 'Fri May 1', 'Sat May 2', 'Sun May 3', 'Mon May 4', 'Tue May 5',
  'Wed May 6', 'Thu May 7', 'Fri May 8', 'Sat May 9', 'Sun May 10', 'Mon May 11', 'Tue May 12',
  'Wed May 13', 'Thu May 14', 'Fri May 15', 'Sat May 16', 'Sun May 17', 'Mon May 18', 'Tue May 19',
];

const FAKE_TREND = {
  ring: {
    this_year: [17.82, 17.88, 17.95, 17.91, 18.05, 18.10, 18.15, 18.22, 18.28, 18.32, 18.38, 18.45, 18.50, 18.54, 18.60, 18.65, 18.68, 18.72, 18.78, 18.82, 18.88, 18.95, 19.02, 19.08, 19.15, 19.22, 19.28, 19.32],
    last_year: [17.78, 17.82, 17.84, 17.86, 17.85, 17.88, 17.86, 17.88, 17.90, 17.85, 17.88, 17.86, 17.89, 17.91, 17.88, 17.90, 17.92, 17.88, 17.91, 17.93, 17.90, 17.92, 17.94, 17.91, 17.93, 17.95, 17.92, 17.94],
    control: [18.04, 18.06, 18.07, 18.08, 18.07, 18.09, 18.08, 18.09, 18.10, 18.08, 18.10, 18.09, 18.10, 18.11, 18.09, 18.10, 18.11, 18.09, 18.10, 18.11, 18.10, 18.11, 18.12, 18.10, 18.11, 18.12, 18.11, 18.13],
    y_min: 17.5, y_max: 19.5, fmt: 'currency' as const,
  },
  ipt: {
    this_year: [1.98, 1.99, 2.01, 2.00, 2.03, 2.04, 2.06, 2.08, 2.09, 2.10, 2.11, 2.12, 2.13, 2.14, 2.16, 2.17, 2.18, 2.19, 2.20, 2.21, 2.22, 2.23, 2.25, 2.27, 2.28, 2.29, 2.30, 2.32],
    last_year: [1.96, 1.97, 1.96, 1.98, 1.97, 1.96, 1.98, 1.97, 1.96, 1.98, 1.97, 1.98, 1.97, 1.98, 1.96, 1.97, 1.98, 1.96, 1.97, 1.98, 1.97, 1.98, 1.96, 1.97, 1.98, 1.97, 1.98, 1.97],
    control: [2.07, 2.08, 2.08, 2.09, 2.08, 2.09, 2.09, 2.08, 2.09, 2.09, 2.08, 2.09, 2.09, 2.10, 2.08, 2.09, 2.10, 2.08, 2.09, 2.10, 2.09, 2.10, 2.09, 2.10, 2.09, 2.10, 2.09, 2.10],
    y_min: 1.85, y_max: 2.40, fmt: 'decimal' as const,
  },
};

export async function getPilotTrend(
  tenantId: string,
  compare: PilotCompare,
): Promise<PilotTrend> {
  if (isFake()) {
    type RawTrend = {
      this_year: number[];
      last_year: number[];
      control: number[];
      y_min: number;
      y_max: number;
      fmt: 'currency' | 'decimal';
    };
    const pick = (m: RawTrend): TrendSeries => ({
      this_year: m.this_year,
      baseline: compare === 'control' ? m.control : m.last_year,
      y_min: m.y_min,
      y_max: m.y_max,
      fmt: m.fmt,
    });
    return {
      dates: FAKE_TREND_DATES,
      iterations: [
        { day_index: 14, label: 'Iter 2' },
        { day_index: 25, label: 'Iter 3' },
      ],
      ring: pick(FAKE_TREND.ring),
      ipt: pick(FAKE_TREND.ipt),
      baseline_label: compare === 'control' ? 'control' : 'last year',
      placeholder: true,
      data_source: 'seeded_trend',
    };
  }
  return api<PilotTrend>(
    `/api/pilot/${encodeURIComponent(tenantId)}/trend?compare=${compare}`,
  );
}

export async function getPilotPerStore(
  tenantId: string,
  compare: PilotCompare,
): Promise<PerStore> {
  if (isFake()) {
    // Mockup §C: two real+seeded-comparison stores and one thin (no-coverage) store.
    const rows: PerStoreRow[] = [
      { store: 'UCSF Millberry Union', ring_display: '$18.91', ring_delta_display: '+4.2%', ipt_display: '2.24', ipt_delta_display: '+0.27', placeholder: true, data_source: 'seeded_baseline' },
      { store: 'SF Financial District', ring_display: '$18.32', ring_delta_display: '+2.8%', ipt_display: '2.17', ipt_delta_display: '+0.19', placeholder: true, data_source: 'seeded_baseline' },
      { store: 'Oakland City Center', ring_display: '—', ring_delta_display: null, ipt_display: '—', ipt_delta_display: null, placeholder: true, data_source: 'no_coverage' },
    ];
    // feat-059 (ADR 0007 D5): under compare=control the backend appends a single
    // combined "Control fleet (transaction-weighted)" footer row. It IS the baseline,
    // so it carries no "vs baseline" delta (deltas null). Real control coverage →
    // pos_real_control (non-placeholder, renders clean); empty control set →
    // no_coverage. last_year is byte-unchanged (no footer row), guarding the shipped
    // real last-year path.
    if (compare === 'control') {
      rows.push({
        store: 'Control fleet (transaction-weighted)',
        ring_display: '$18.09',
        ring_delta_display: null,
        ipt_display: '2.09',
        ipt_delta_display: null,
        placeholder: false,
        data_source: 'pos_real_control',
      });
    }
    return {
      rows,
      total: { store: 'Pilot total', ring_display: '$18.42', ring_delta_display: '+3.1%', ipt_display: '2.18', ipt_delta_display: '+0.21', placeholder: true, data_source: 'seeded_baseline' },
      placeholder: true,
      data_source: 'seeded_baseline',
    };
  }
  return api<PerStore>(
    `/api/pilot/${encodeURIComponent(tenantId)}/per-store?compare=${compare}`,
  );
}

export async function getPilotAdjustments(tenantId: string): Promise<PilotAdjustments> {
  if (isFake()) {
    return {
      rows: [
        { id: 'a-1', description: 'Menu price increase (avg +2.1% across pilot stores, Mar 2026)', note: 'Baseline adjusted up to reflect comparable pricing.', impact_display: '+$0.37 on baseline ring', flagged: false, created_by_name: 'Dana R.', effective_date: '2026-03-01' },
        { id: 'a-2', description: 'New seasonal entree launched Apr 2026', note: 'Excluded from both periods until 12 months of comparable data.', impact_display: 'no net effect', flagged: false, created_by_name: 'Dana R.', effective_date: '2026-04-01' },
        { id: 'a-3', description: 'Staffing change at SF Financial District (added closing shift Apr 14)', note: 'Baseline weighted to match current shift coverage.', impact_display: '+$0.08 on baseline ring', flagged: false, created_by_name: 'Marco T.', effective_date: '2026-04-14' },
        { id: 'a-4', description: 'Competitor opening near Oakland City Center (Mar 2026)', note: 'Flagged. Not normalized yet pending 60-day local trend data.', impact_display: 'flagged', flagged: true, created_by_name: 'Marco T.', effective_date: '2026-03-15' },
      ],
      net_display: '+$0.45',
      // b1 fake path shows the seeded sample (mockup §D). Real rows would carry
      // placeholder:false / data_source:"manager_entered".
      placeholder: true,
      data_source: 'seeded_adjustments',
    };
  }
  return api<PilotAdjustments>(`/api/pilot/${encodeURIComponent(tenantId)}/adjustments`);
}
